import React from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useTour } from '../contexts/TourContext';

// Define the styles for the tour
const tourStyles = {
  options: {
    zIndex: 10000,
    arrowColor: '#1a1a1a',
    backgroundColor: '#1a1a1a',
    overlayColor: 'rgba(0, 0, 0, 0.7)',
    primaryColor: '#5865F2', // Primary color from SuprDrive
    textColor: '#ffffff',
    width: 350, // Width of the tooltip
    beaconSize: 36, // Size of the beacon
  },
  tooltipContainer: {
    textAlign: 'left',
    padding: 16
  },
  tooltipTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: 10,
    color: '#ffffff'
  },
  tooltipContent: {
    fontSize: '0.95rem',
    lineHeight: 1.5
  },
  buttonBack: {
    fontSize: '14px',
    padding: '8px 16px',
    color: '#ffffff',
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    marginRight: 8,
    outline: 'none'
  },
  buttonNext: {
    fontSize: '14px',
    padding: '8px 16px',
    color: '#ffffff',
    backgroundColor: '#5865F2',
    border: 'none',
    borderRadius: '6px',
    boxShadow: '0px 3px 8px rgba(88, 101, 242, 0.3)',
    outline: 'none'
  },
  buttonSkip: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.6)',
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none'
  }
};

const GuidedTour = () => {
  const { 
    isTourOpen, 
    tourSteps, 
    stepIndex, 
    setStepIndex, 
    completeTour
  } = useTour();
  
  // Debug mode - help diagnose issues
  const DEBUG_MODE = true;
  
  const debugLog = (message, data) => {
    if (DEBUG_MODE) {
      // console.log(`Tour Debug: ${message}`, data);
    }
  };

  // Handle tour state changes
  const handleJoyrideCallback = (data) => {
    const { action, index, status, type, step } = data;

    debugLog(`Callback [${type}:${action}] at step ${index}`, data);

    // Handle different types of events
    switch (type) {
      case EVENTS.STEP_AFTER:
        if (action === ACTIONS.NEXT) {
          // Handle next button click
          debugLog('Next button pressed', { currentIndex: index, newIndex: index + 1 });
          setStepIndex(index + 1);
        } else if (action === ACTIONS.PREV) {
          // Handle back button click - go to previous step
          debugLog('Back button pressed', { currentIndex: index, newIndex: index - 1 });
          if (index > 0) {
            setStepIndex(index - 1);
          }
        }
        break;
        
      case EVENTS.TOUR_STATUS:
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
          debugLog('Tour ended', { status });
          completeTour();
        }
        break;
        
      case EVENTS.TOUR_END:
        // Tour ended by user
        debugLog('Tour manually ended');
        completeTour();
        break;
        
      case EVENTS.TARGET_NOT_FOUND:
        // Skip to next step if target isn't found
        debugLog('Target not found', { step: step?.target });
        setStepIndex(index + 1);
        break;
      
      default:
        break;
    }
  };

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous={true}
      hideCloseButton={true}
      run={isTourOpen}
      scrollToFirstStep={true}
      showProgress={true}
      showSkipButton={true}
      steps={tourSteps}
      stepIndex={stepIndex}
      styles={tourStyles}
      disableCloseOnEsc={false}
      disableOverlayClose={true}
      spotlightClicks={false}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        open: 'Open the tour',
        skip: 'Skip tour'
      }}
    />
  );
};

export default GuidedTour; 